import { BiShapeTriangle } from 'react-icons/bi'
import FancyProgressArc from './FancyProgressArc'
import GenerateTraceButton from './buttons/GenerateTraceButton'
import { openLowTraceSentencesModal } from '../services/Modals'
import type { TextStructure } from '../types/textstructure'
import type { Question } from '../types'

interface TraceArcProps {
  // The sim/trace percent (0–100). Optional — defaults to
  // `question.answerobj.simint` (with a `>= 0` guard so the app's
  // "-1 == not yet generated" convention still renders as N/A).
  // Pass explicitly to override.
  value?: number | null | undefined
  scale?: number
  tooltip?: string
  // Structure whose low-sim sentences feed the default click modal.
  // When set, clicking the arc opens the read-only popout listing
  // every sentence below the trace threshold (rendered by the
  // centralised `ModalHost`). When omitted, clicks do nothing
  // unless `onClick` is supplied.
  structure?: TextStructure | null
  // Optional override for the modal title.
  modalTitle?: string
  // Override the click behaviour. When set the default low-trace
  // modal is skipped so callers can wire their own popout.
  onClick?: () => void
  // While true the arc swaps its centre label for a CircularProgress
  // spinner — callers no longer have to flip between a sibling
  // CircularProgress and the arc to show "tracing in progress".
  loading?: boolean
  // When set and the arc is in the N/A state (no sim yet), the centre
  // renders a `GenerateTraceButton` for this question instead of the
  // "N/A" text — so the arc itself becomes the affordance to compute
  // the missing trace. The arc also pulls its default `structure`
  // from `question.answerobj.answerstructure` (so the low-trace
  // modal opens on click without having to thread the structure
  // through explicitly — same pattern as ScoreArc's `question`).
  question?: Question | null
}

// Thin wrapper around FancyProgressArc preconfigured for the per-question
// trace similarity. Renders a single arc for a single question — callers
// that want to show a "before → after" comparison use `TraceArcPair`
// instead. Pass `structure` (or a `question` carrying one) to open the
// low-trace-sentences modal on click; pass `onClick` to take over the
// click behaviour entirely.
export default function TraceArc({
  value, scale = 1, tooltip, structure, modalTitle, onClick, loading, question,
}: TraceArcProps) {
  // Resolve the arc value. Explicit `value` wins; otherwise derive from
  // `question.answerobj.simint`. `-1` (and other negatives) fall back
  // to `undefined` so the arc renders as N/A — matches the codebase's
  // "-1 == not yet generated" convention.
  const derivedSimint = question?.answerobj?.simint
  const resolvedValue: number | null | undefined = value !== undefined
    ? value
    : (typeof derivedSimint === 'number' && derivedSimint >= 0 ? derivedSimint : undefined)
  // Resolve the modal-source structure: `structure` wins, falling back
  // to `question.answerobj.answerstructure` so callers can hand the
  // arc just a question and get the low-trace modal for free.
  const effectiveStructure = structure ?? question?.answerobj?.answerstructure ?? null

  const defaultClick = effectiveStructure
    ? () => openLowTraceSentencesModal({
      structure: effectiveStructure,
      simint: resolvedValue,
      title: modalTitle ?? tooltip,
    })
    : undefined
  // When the arc is in the N/A state AND we're rendering the
  // GenerateTraceButton as the centre slot, the arc-level click is
  // suppressed — the user's click should land on the button (which
  // fires its own runTrace pipeline) rather than open the low-trace
  // modal against an empty trace.
  const isNA = resolvedValue == null
  const showGenerateButton = isNA && !!question
  const effectiveClick = showGenerateButton ? undefined : (onClick ?? defaultClick)
  return (
    <FancyProgressArc
      value={resolvedValue}
      scale={scale}
      icon={<BiShapeTriangle size={14} style={{ opacity: 0.6 }} />}
      tooltip={tooltip ?? ''}
      loading={loading}
      naSlot={question ? <GenerateTraceButton question={question} iconSize={Math.round(16 * scale)} /> : undefined}
      {...(effectiveClick ? { onClick: effectiveClick, style: { cursor: 'pointer' } } : null)}
    />
  )
}
